import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateNewsDto,
  UpdateNewsDto,
  getNews,
  getNewsById,
  createNews,
  updateNews,
  deleteNews,
  getPublicNews,
} from '../services/newsService';

export const useGetNews = (page: number = 1, limit: number = 100) => {
  return useQuery({
    queryKey: ['news', page, limit],
    queryFn: () => getNews(page, limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useGetNewsById = (id: number) => {
  return useQuery({
    queryKey: ['news', id],
    queryFn: () => getNewsById(id),
    enabled: !!id,
  });
};

export const useCreateNews = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateNewsDto) => createNews(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
    },
  });
};

export const useUpdateNews = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateNewsDto }) => updateNews(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      queryClient.invalidateQueries({ queryKey: ['news', variables.id] });
    },
  });
};

export const useDeleteNews = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteNews(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
    },
  });
};

export const useGetPublicNews = (page: number = 1, limit: number = 3) => {
  return useQuery({
    queryKey: ['public-news', page, limit],
    queryFn: () => getPublicNews(page, limit),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};
